"use client";

import { CampaignCard } from "@/components/featured/common";
import NotFoundState from "@/components/ui/NotFoundState";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { sendGTMEvent } from "@next/third-parties/google";
import { useEffect, useMemo, useState } from "react";
import CampaignsSearch from "./CampaignsSearch";

export default function CampaignsClientSection({ campaigns }) {
  const t = useTranslationDetector();
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCampaigns = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return campaigns;

    return campaigns.filter((c) => {
      const title = (c.TitleWithoutHtmlTags || c.Title || "").toLowerCase();
      const desc = (c.MainTextWithoutHtmlTags || "").toLowerCase();
      const subtitle = (c.Subtitle || "").toLowerCase();
      const campaignCode = (c.CampaignCode || "").toLowerCase();

      return (
        title.includes(term) ||
        desc.includes(term) ||
        subtitle.includes(term) ||
        campaignCode.includes(term)
      );
    });
  }, [campaigns, searchTerm]);

  const pushDataLayerEvent = () => {
    sendGTMEvent({ ecommerce: null });
    sendGTMEvent({
      event: "ecom_event",
      event_action: "view_promotion",
      ecommerce: {
        items: campaigns.map((campaign, index) => ({
          creative_name: campaign?.Subtitle || campaign?.Title || "",
          creative_slot: `Body ${index + 1}`,
          promotion_id: campaign?.CampaignId,
          promotion_name:
            campaign?.TitleWithoutHtmlTags || campaign?.Title || "",
        })),
      },
    });
  };

  useEffect(() => {
    pushDataLayerEvent();
  }, [campaigns]);

  return (
    <div className="container mx-auto flex flex-col gap-8 pt-12 pb-[72px]">
      <div className="flex items-center justify-between">
        <h1 className="text-[29px]/[38px] font-semibold">
          {t("campaigns.campaignsTitle")}
        </h1>
        <CampaignsSearch onSearch={setSearchTerm} />
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
        {filteredCampaigns.length > 0 ? (
          filteredCampaigns.map((campaign, index) => (
            <CampaignCard
              key={campaign.CampaignId}
              item={campaign}
              selectPromotionPayload={{
                creative_name: campaign?.Subtitle || campaign?.Title || "",
                creative_slot: `Body ${index + 1}`,
                promotion_id: campaign?.CampaignId,
                promotion_name:
                  campaign?.TitleWithoutHtmlTags || campaign?.Title || "",
              }}
            />
          ))
        ) : (
          <div className="col-span-full">
            <NotFoundState
              title={t("campaigns.noResultsTitle")}
              description={t("campaigns.noResultsDescription")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
